// goalPace.js — THE pacing words for period goals (2026-10-17), shared by the
// cron's computeStatus (api/lib/coach-run.js), the chat's goal lines, and the
// scoring's goal-adherence buckets (src/lib/goalAdherence.js), so "behind" on
// a card, in a check-in and in a score all mean the same thing.
import { weekPosition } from './weekWindow.js';

/**
 * Weekly COUNT goal (workouts a week), Monday-start week: paced by where
 * today sits. Monday 0/4 is on_track; Thursday 1/4 is behind; Saturday 1/4
 * is at_risk (more left than days left, today included). >= target is hit.
 */
export function weeklyCountStatus({ current, target, today }) {
  const t = Number(target), c = Number(current) || 0;
  if (!(t > 0)) return null;
  if (c >= t) return 'hit';
  const pos = weekPosition(today);
  if (!pos) {
    const ratio = c / t;
    return ratio >= 0.9 ? 'on_track' : ratio >= 0.6 ? 'behind' : 'at_risk';
  }
  const remaining = t - c;
  if (remaining > pos.daysLeft) return 'at_risk';
  // Expected-by-now rounds DOWN: Monday's share of 4 is 0.57, so 0 is on
  // track; by Thursday it is 2.29, so 2 is on track and 1 is behind.
  const expected = Math.floor(t * (pos.daysElapsed / 7));
  return c >= expected ? 'on_track' : 'behind';
}

/**
 * Monthly CAP (spending): lower is better. At or over the cap is at_risk;
 * otherwise the share used is compared with the share of the month elapsed.
 */
export function monthlyCapStatus({ current, target, fractionElapsed }) {
  const t = Number(target), c = Number(current) || 0;
  if (!(t > 0)) return null;
  const ratio = c / t;
  if (ratio >= 1) return 'at_risk';
  const f = Number(fractionElapsed) || 0;
  if (ratio <= f - 0.15) return 'ahead';
  if (ratio <= f + 0.10) return 'on_track';
  if (ratio < 0.9) return 'behind';
  return 'at_risk';
}

/** Local 'YYYY-MM-DD' → { month: 'YYYY-MM', day, daysInMonth, fractionElapsed }. */
export function monthPositionOf(todayStr) {
  const [y, m, d] = String(todayStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { month: `${y}-${String(m).padStart(2, '0')}`, day: d, daysInMonth, daysLeft: daysInMonth - d, fractionElapsed: d / daysInMonth };
}

// Points a pace word is worth, 0..1, for the scoring's adherence buckets.
export const COUNT_STATUS_POINTS = { hit: 1, ahead: 1, on_track: 0.85, behind: 0.5, at_risk: 0.2 };
export const CAP_STATUS_POINTS = { ahead: 1, on_track: 0.8, behind: 0.4, at_risk: 0 };
